import { collectKeysFromUserRoles } from "./flattenPermissionKeys";
import {
	getFieldCRUD,
	hasPermission,
	isAnanunakiSuperuser
} from "./permissionResolver";

const CHAT_ACTION_MODES = new Set(["allow", "deny", "timed"]);
const CHAT_ACTION_KEYS = ["forward", "reply", "edit"];

const CHAT_FEATURE_PERMISSION_KEYS = {
	createGroup: ["chat.createGroup.update", "chat.create"],
	users: ["chat.users.create", "chat.users.update", "chat.users.delete"],
	sendMessage: ["chat.sendMessage.update"],
	pin: ["chat.pin.update"],
	star: ["chat.star.update"],
	deleteMessage: ["chat.deleteMessage.update"],
	// Per-chat sidebar actions (default-allow, deny-configurable). Intentionally
	// NOT added to CHAT_FEATURE_FIELDS so resolution mirrors the backend legacy/
	// flat-key path instead of requiring module-level chat.update.
	clearHistory: ["chat.clearHistory.update"],
	deleteChat: ["chat.deleteChat.update"],
	attachFile: ["chat.attachFile.update"],
	calling: ["chat.calling.update"],
	videoCalling: ["chat.videoCalling.update"],
	searchFiles: ["chat.searchFiles.update"],
	searchMessages: ["chat.searchMessages.update"],
	transcribe: ["chat.transcribe.update"],
	transcription: ["chat.transcription.read"],
	viewTranscription: ["chat.transcription.read"],
	// Call feature permissions
	"call.audio.start": ["chat.callAudioStart.update"],
	"call.video.start": ["chat.callVideoStart.update"],
	"call.conference.start": ["chat.callConferenceStart.update"],
	"call.join": ["chat.callJoin.update"],
	"call.invite": ["chat.callInvite.update"],
	"call.end.own": ["chat.callEndOwn.update"],
	"call.end.any": ["chat.callEndAny.update"],
	"call.screenShare": ["chat.callScreenShare.update"],
	"call.mute.self": ["chat.callMuteSelf.update"],
	"call.mute.others": ["chat.callMuteOthers.update"],
	"call.removeParticipant": ["chat.callRemoveParticipant.update"],
	"call.viewHistory": ["chat.callViewHistory.update"],
	"call.manageSettings": ["chat.callManageSettings.update"]
};

const CHAT_FEATURE_FIELDS = {
	createGroup: ["createGroup"],
	users: ["users"],
	sendMessage: ["sendMessage"],
	pin: ["pin"],
	star: ["star"],
	deleteMessage: ["deleteMessage"],
	attachFile: ["attachFile"],
	calling: ["calling"],
	videoCalling: ["videoCalling"],
	searchFiles: ["searchFiles"],
	searchMessages: ["searchMessages"],
	transcribe: ["transcribe"],
	transcription: ["transcription"],
	viewTranscription: ["transcription"],
	"call.audio.start": ["callAudioStart", "calling"],
	"call.video.start": ["callVideoStart", "videoCalling"],
	"call.conference.start": ["callConferenceStart", "calling", "videoCalling"],
	"call.join": ["callJoin"],
	"call.invite": ["callInvite"],
	"call.end.own": ["callEndOwn"],
	"call.end.any": ["callEndAny"],
	"call.screenShare": ["callScreenShare"],
	"call.mute.self": ["callMuteSelf"],
	"call.mute.others": ["callMuteOthers"],
	"call.removeParticipant": ["callRemoveParticipant"],
	"call.viewHistory": ["callViewHistory"],
	"call.manageSettings": ["callManageSettings"]
};

const CHAT_FEATURE_LEGACY_ALIASES = {
	createGroup: ["createGroup"],
	users: ["users"],
	sendMessage: ["sendMessage"],
	pin: ["pin"],
	star: ["star"],
	deleteMessage: ["deleteMessage"],
	clearHistory: ["clearHistory"],
	deleteChat: ["deleteChat"],
	attachFile: ["attachFile"],
	calling: ["calling"],
	videoCalling: ["videoCalling"],
	transcribe: ["transcribe"],
	transcription: ["transcription", "viewTranscription"],
	viewTranscription: ["transcription", "viewTranscription"],
	// Call feature permissions have no legacy aliases — flat keys only
	"call.audio.start": [],
	"call.video.start": [],
	"call.conference.start": [],
	"call.join": [],
	"call.invite": [],
	"call.end.own": [],
	"call.end.any": [],
	"call.screenShare": [],
	"call.mute.self": [],
	"call.mute.others": [],
	"call.removeParticipant": [],
	"call.viewHistory": [],
	"call.manageSettings": []
};

function mergedDeny(user) {
	const s = new Set();
	(user?.permissions?.deny || []).forEach((key) => s.add(key));
	(user?.permissionOverrides?.deny || []).forEach((key) => s.add(key));
	return s;
}

function mergedAllow(user) {
	const s = new Set();
	(user?.permissions?.allow || []).forEach((key) => s.add(key));
	(user?.permissionOverrides?.allow || []).forEach((key) => s.add(key));
	return s;
}

function getUserRoles(user = {}) {
	const roles = Array.isArray(user?.role)
		? user.role
		: user?.role
			? [user.role]
			: [];
	return roles.filter((role) => role && typeof role === "object");
}

function permissionGrantEnabled(entry = {}) {
	return Boolean(entry?.create || entry?.read || entry?.update || entry?.delete);
}

function getChatPermissionEntries(user = {}) {
	return getUserRoles(user)
		.map((role) =>
			(role?.permissions || []).find((permission) => permission?.name === "chat")
		)
		.filter(Boolean);
}

function getFeaturePermissionKeys(featureName) {
	return CHAT_FEATURE_PERMISSION_KEYS[featureName] || [];
}

function hasMappedFlatKeyConfig(user = {}, featureName) {
	const keys = getFeaturePermissionKeys(featureName);
	if (!keys.length) return false;

	const roleKeys = collectKeysFromUserRoles(user);
	const allow = mergedAllow(user);
	const deny = mergedDeny(user);

	return keys.some(
		(permissionKey) =>
			roleKeys.has(permissionKey) ||
			allow.has(permissionKey) ||
			deny.has(permissionKey)
	);
}

function getLegacyFeatureAliases(featureName) {
	return CHAT_FEATURE_LEGACY_ALIASES[featureName] || [featureName];
}

function getLegacyChatFeaturePermission(user = {}, featureName) {
	const chatPermissions = getChatPermissionEntries(user);
	if (!chatPermissions.length) return null;

	const featureAliases = getLegacyFeatureAliases(featureName);
	let sawExplicitInput = false;
	let explicitAllowed = false;
	let moduleAllowed = false;

	chatPermissions.forEach((permission) => {
		if (permissionGrantEnabled(permission?.crud)) {
			moduleAllowed = true;
		}

		const matchedInput = (permission?.inputs || []).find((input) =>
			featureAliases.includes(input?.name)
		);
		if (!matchedInput) return;

		sawExplicitInput = true;
		if (permissionGrantEnabled(matchedInput)) {
			explicitAllowed = true;
		}
	});

	if (sawExplicitInput) return explicitAllowed;
	if (["transcribe", "transcription", "viewTranscription"].includes(featureName)) return false;
	return moduleAllowed;
}

export function getChatFeatureAccess(user = {}, featureName, options = {}) {
	const { defaultValue = true } = options;
	const fallbackAccess = {
		create: defaultValue,
		read: defaultValue,
		update: defaultValue,
		delete: defaultValue
	};

	if (!featureName) return fallbackAccess;
	if (isAnanunakiSuperuser(user) || user?.ananunaki) {
		return {
			create: true,
			read: true,
			update: true,
			delete: true
		};
	}

	const mappedFields = CHAT_FEATURE_FIELDS[featureName] || [];
	if (mappedFields.length) {
		return mappedFields.reduce(
			(access, fieldName) => {
				const crud = getFieldCRUD(user, "chat", fieldName);
				return {
					create: access.create || Boolean(crud?.create),
					read: access.read || Boolean(crud?.read),
					update: access.update || Boolean(crud?.update),
					delete: access.delete || Boolean(crud?.delete)
				};
			},
			{ create: false, read: false, update: false, delete: false }
		);
	}

	const permissionKeys = getFeaturePermissionKeys(featureName);
	if (permissionKeys.length && hasMappedFlatKeyConfig(user, featureName)) {
		return {
			create: permissionKeys.some((permissionKey) => hasPermission(user, permissionKey)),
			read: permissionKeys.some((permissionKey) =>
				hasPermission(user, permissionKey.replace(/\.update$/, ".read"))
			),
			update: permissionKeys.some((permissionKey) => hasPermission(user, permissionKey)),
			delete: permissionKeys.some((permissionKey) =>
				hasPermission(user, permissionKey.replace(/\.update$/, ".delete"))
			)
		};
	}

	const legacyResult = getLegacyChatFeaturePermission(user, featureName);
	if (legacyResult !== null) {
		return {
			create: legacyResult,
			read: legacyResult,
			update: legacyResult,
			delete: legacyResult
		};
	}

	if (permissionKeys.length) {
		return {
			create: permissionKeys.some((permissionKey) => hasPermission(user, permissionKey)),
			read: permissionKeys.some((permissionKey) =>
				hasPermission(user, permissionKey.replace(/\.update$/, ".read"))
			),
			update: permissionKeys.some((permissionKey) => hasPermission(user, permissionKey)),
			delete: permissionKeys.some((permissionKey) =>
				hasPermission(user, permissionKey.replace(/\.update$/, ".delete"))
			)
		};
	}

	return fallbackAccess;
}

export function getAnyChatFeatureAccess(user = {}, featureNames = [], options = {}) {
	const { defaultValue = true } = options;
	const resolvedFeatures = Array.isArray(featureNames) ? featureNames.filter(Boolean) : [];
	if (!resolvedFeatures.length) {
		return {
			create: defaultValue,
			read: defaultValue,
			update: defaultValue,
			delete: defaultValue
		};
	}

	return resolvedFeatures.reduce(
		(access, featureName) => {
			const featureAccess = getChatFeatureAccess(user, featureName, { defaultValue });
			return {
				create: access.create || featureAccess.create,
				read: access.read || featureAccess.read,
				update: access.update || featureAccess.update,
				delete: access.delete || featureAccess.delete
			};
		},
		{ create: false, read: false, update: false, delete: false }
	);
}

export function hasChatFeaturePermission(user = {}, featureName, options = {}) {
	const { action = "update", defaultValue = true } = options;
	if (!featureName) return defaultValue;
	return getChatFeatureAccess(user, featureName, { defaultValue })?.[action] ?? defaultValue;
}

export function normalizeChatActionRule(rule = {}) {
	const mode = CHAT_ACTION_MODES.has(rule?.mode) ? rule.mode : "allow";
	const durationMinutes = Number(rule?.durationMinutes);
	return {
		mode,
		durationMinutes:
			mode === "timed" && Number.isFinite(durationMinutes) && durationMinutes > 0
				? durationMinutes
				: null
	};
}

export function isDefaultChatActionRule(rule = {}) {
	const normalized = normalizeChatActionRule(rule);
	return normalized.mode === "allow" && normalized.durationMinutes === null;
}

export function hasCustomChatActionSettings(chat = {}) {
	const explicitFlag = chat?.settings?.hasCustomActionSettings;
	if (typeof explicitFlag === "boolean") return explicitFlag;

	const actions = chat?.settings?.actions || {};
	return CHAT_ACTION_KEYS.some((actionKey) => !isDefaultChatActionRule(actions?.[actionKey]));
}

export function getChatActionRule(chat = {}, actionKey, options = {}) {
	const globalSettings = options?.globalSettings || null;
	if (chat?.chatType && chat.chatType !== "direct") {
		if (!hasCustomChatActionSettings(chat)) return normalizeChatActionRule();
		return normalizeChatActionRule(chat?.settings?.actions?.[actionKey]);
	}
	if (globalSettings?.actions) {
		return normalizeChatActionRule(globalSettings.actions?.[actionKey]);
	}
	return normalizeChatActionRule();
}

export function getMessageTimestampMs(message = {}) {
	const timestamp = message?.createdAt || message?.created || message?.updatedAt || null;
	const ms = timestamp ? new Date(timestamp).getTime() : 0;
	return Number.isFinite(ms) ? ms : 0;
}

export function isChatActionAllowed({
	chat = {},
	actionKey,
	message = {},
	now = Date.now(),
	globalSettings = null
} = {}) {
	const rule = getChatActionRule(chat, actionKey, { globalSettings });
	if (rule.mode === "allow") return true;
	if (rule.mode === "deny") return false;

	const durationMs = Number(rule.durationMinutes || 0) * 60 * 1000;
	const timestampMs = getMessageTimestampMs(message);
	if (!durationMs || !timestampMs) return false;

	return now - timestampMs <= durationMs;
}

const chatPermissionUtils = {
	getAnyChatFeatureAccess,
	getChatFeatureAccess,
	hasChatFeaturePermission,
	normalizeChatActionRule,
	isDefaultChatActionRule,
	hasCustomChatActionSettings,
	getChatActionRule,
	getMessageTimestampMs,
	isChatActionAllowed
};

export default chatPermissionUtils;
